/**
 * components/riskTerminal/usePanel.js
 *
 * upstox-054: the panel arrives over the live stream (liveStream.js) —
 * subscribed in hidden tabs too, so the screen is current on return. A
 * `panel` event replaces it, a `patch` replaces the changed top-level keys,
 * a heartbeat proves it current. One GET /upstox/panel at the start (the
 * stream's first event is usually first anyway) and after an action
 * (refresh); while the stream is down for 30 s ("polling"), GET every 5 s
 * until it returns. A failed GET keeps the last good panel and reports the
 * failure. A 1 s clock drives the data age and the running timers.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribe } from './liveStream';
import { withAccount } from './accounts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const POLL_MS = 5000;

export default function usePanel(accountKey) {
  const [panel, setPanel] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [stream, setStream] = useState(null);
  const inFlight = useRef(false);
  const again = useRef(false);
  const gen = useRef(0);

  // a GET built before the panel we already hold never replaces it
  const accept = useCallback(body => {
    setPanel(prev => (prev && prev.computed_at && body.computed_at && body.computed_at < prev.computed_at ? prev : body));
    setFetchedAt(Date.now());
    setError(null);
  }, []);

  const poll = useCallback(async () => {
    if (inFlight.current) { again.current = true; return; }
    inFlight.current = true;
    const mine = gen.current;
    try {
      const res = await fetch(`${API}/upstox${withAccount('/panel')}`, { credentials: 'include' });
      if (!res.ok) throw new Error(res.status === 401 ? 'signed out' : 'HTTP ' + res.status);
      const body = await res.json();
      if (mine === gen.current) accept(body);            // a reply for the account we left is dropped
    } catch (e) {
      setError(e.message || 'network error');
    } finally {
      inFlight.current = false;
      if (again.current) { again.current = false; poll(); }
    }
  }, [accept]);

  // upstox-067: a different account — nothing of the old one is kept: clear, then load
  useEffect(() => {
    gen.current += 1;
    setPanel(null); setFetchedAt(null); setError(null);
    again.current = false;
  }, [accountKey]);

  useEffect(() => {
    const off = subscribe({
      panel: p => { setPanel(p); setFetchedAt(Date.now()); setError(null); },
      patch: d => { setPanel(prev => (prev ? { ...prev, ...d } : prev)); setFetchedAt(Date.now()); },
      heartbeat: () => setFetchedAt(Date.now()),
      state: s => setStream(s),
    });
    poll();
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => { off(); clearInterval(c); };
  }, [poll, accountKey]);

  // the stream down for 30 s: poll until it returns
  const polling = stream && stream.mode === 'polling';
  useEffect(() => {
    if (!polling) return undefined;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [polling, poll]);

  // after a successful action the server pushes the rebuilt panel; this GET
  // is the same panel by request (and the only way while polling)
  const refresh = useCallback(() => poll(), [poll]);

  return { panel, fetchedAt, error, now, refresh, stream };
}
